import { groupBy } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Checkbox, DataTable, DataTableBody } from '@optiaxiom/react';
import { DataTableExpandableCell, DataTableExpandableHeader } from '@optiaxiom/react/unstable';
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  HeaderContext,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import NameCell from '@frontend/components/tools-table/cells/NameCell';
import { ToolsTableRow } from '@frontend/components/tools-table/types';
import { Tool } from '@frontend/types';

import { styles } from './AddToolTable.css';

type AddToolTableProps = {
  tools: Tool[];
  selectedToolIds: string[];
  disabledToolNames: string[];
  onSelectTool: (toolId: string) => void;
  onSelectRegistry: (toolIds: string[]) => void;
  onSelectAll: (toolIds: string[]) => void;
  onToolHighlight: (tool: Tool | null) => void;
};

type SelectHeaderProps = {
  allSelectableToolIds: string[];
  onSelectAll: (toolIds: string[]) => void;
};

const SelectHeader = memo(function SelectHeader({
  context,
  allSelectableToolIds,
  onSelectAll,
}: SelectHeaderProps & { context: HeaderContext<ToolsTableRow, unknown> }) {
  const allSelected = context.table.getIsAllRowsSelected();
  const someSelected = context.table.getIsSomeRowsSelected();

  return (
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      disabled={allSelectableToolIds.length === 0}
      onCheckedChange={() => onSelectAll(allSelectableToolIds)}
    />
  );
});

type SelectCellProps = {
  context: CellContext<ToolsTableRow, unknown>;
  disabledToolNames: string[];
  onSelectTool: (toolId: string) => void;
  onSelectRegistry: (toolIds: string[]) => void;
};

const SelectCell = memo(function SelectCell({
  context,
  disabledToolNames,
  onSelectTool,
  onSelectRegistry,
}: SelectCellProps) {
  const { row } = context;
  const rowData = row.original;

  if (rowData.isRegistry) {
    const selectableToolIds = rowData.items
      .filter(item => !item.isRegistry && !disabledToolNames.includes(item.tool.name))
      .map(item => item.id);

    if (selectableToolIds.length === 0) {
      return <Checkbox disabled checked={false} />;
    }

    const selectedCount = row.subRows.filter(subRow => subRow.getIsSelected()).length;
    const allSelected = selectedCount === selectableToolIds.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onCheckedChange={() => onSelectRegistry(selectableToolIds)}
      />
    );
  }

  const isDisabled = disabledToolNames.includes(rowData.tool.name);
  const isSelected = row.getIsSelected();

  return (
    <Checkbox
      checked={isDisabled || isSelected}
      disabled={isDisabled}
      onCheckedChange={() => onSelectTool(rowData.id)}
    />
  );
});

type NameCellWrapperProps = {
  context: CellContext<ToolsTableRow, string>;
  onToolHighlight: (tool: Tool | null) => void;
};

const NameCellWrapper = memo(function NameCellWrapper({
  context,
  onToolHighlight,
}: NameCellWrapperProps) {
  return (
    <DataTableExpandableCell {...context}>
      <NameCell toolRow={context.row.original} onRowSelection={onToolHighlight} />
    </DataTableExpandableCell>
  );
});

const columnHelper = createColumnHelper<ToolsTableRow>();

export function AddToolTable({
  tools,
  selectedToolIds,
  disabledToolNames,
  onSelectTool,
  onSelectRegistry,
  onSelectAll,
  onToolHighlight,
}: AddToolTableProps) {
  const data = useMemo((): ToolsTableRow[] => {
    const grouped = groupBy(tools, tool => tool.service.id);
    return Object.values(grouped).map(registryTools => ({
      id: registryTools[0].service.id,
      name: registryTools[0].service.name,
      isRegistry: true,
      registry: registryTools[0].service,
      items: registryTools.map(tool => ({
        id: tool.function_id,
        name: tool.name,
        isRegistry: false,
        tool,
        items: [],
      })),
    }));
  }, [tools]);

  const allSelectableToolIds = useMemo(() => {
    return tools
      .filter(tool => !disabledToolNames.includes(tool.name))
      .map(tool => tool.function_id);
  }, [tools, disabledToolNames]);

  const rowSelection = useMemo((): RowSelectionState => {
    return selectedToolIds.reduce<RowSelectionState>((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }, [selectedToolIds]);

  const getRowId = useCallback((row: ToolsTableRow) => row.id, []);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        size: 5,
        minSize: 5,
        maxSize: 5,
        header: context => (
          <SelectHeader
            context={context}
            allSelectableToolIds={allSelectableToolIds}
            onSelectAll={onSelectAll}
          />
        ),
        cell: context => (
          <SelectCell
            context={context}
            disabledToolNames={disabledToolNames}
            onSelectTool={onSelectTool}
            onSelectRegistry={onSelectRegistry}
          />
        ),
      }),
      columnHelper.accessor('name', {
        header: () => <DataTableExpandableHeader>Registry / Tool Name</DataTableExpandableHeader>,
        cell: context => <NameCellWrapper context={context} onToolHighlight={onToolHighlight} />,
      }),
    ],
    [
      allSelectableToolIds,
      disabledToolNames,
      onSelectTool,
      onSelectRegistry,
      onSelectAll,
      onToolHighlight,
    ]
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.items,
    getRowId,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
  });

  return (
    <DataTable table={table} className={styles.table}>
      <DataTableBody />
    </DataTable>
  );
}
